import React, { useContext, useState, useEffect, useMemo } from "react";
import "./Complaint.css";
import { useTranslation } from "react-i18next";
import { _t, getCookie, customStyle } from "../../../../functions/Functions";
import { Helmet } from "react-helmet";
import ComplaintsSidebar from "./ComplaintsSidebar";
import { UserContext } from "../../../../contexts/User";
import { AuditContext } from "../../../../contexts/Audit";
import { RestaurantContext } from "../../../../contexts/Restaurant";
import CustomSpinLoader from "../../../components/CustomSpinLoader";
import axios from "axios";
import { BASE_URL } from "../../../../BaseUrl";
import moment from "moment";
import Select from "react-select";
import DatePicker from "react-datepicker";
import "react-datepicker/dist/react-datepicker.css";

export default function ComplaintReport() {
  const { t } = useTranslation();
  const { authUserInfo } = useContext(UserContext);
  const { loading: auditLoading } = useContext(AuditContext);
  const { branchForSearch } = useContext(RestaurantContext);

  const [{ data, loading }, setReportState] = useState({ data: null, loading: true });
  const [fromDate, setFromDate] = useState(new Date());
  const [toDate, setToDate] = useState(new Date());
  const [branch, setBranch] = useState(null);

  const getReport = () => {
    let isMounted = true;
    setReportState(prev => ({ ...prev, loading: true }));
    
    const payload = {
      from_date: moment(fromDate).format("YYYY-MM-DD"),
      to_date: moment(toDate).format("YYYY-MM-DD"),
      ...(branch?.id && { branch_id: branch.id })
    };

    axios.post(`${BASE_URL}/complaints/reports`, payload, {
      headers: { Authorization: `Bearer ${getCookie()}` },
    })
    .then(response => {
      if (isMounted) {
        setReportState({ data: response.data?.status ? response.data : null, loading: false });
      }
    })
    .catch(error => {
      console.error("Error fetching complaint reports:", error);
      if (isMounted) setReportState(prev => ({ ...prev, loading: false }));
    });
    
    return () => { isMounted = false; };
  };

  useEffect(() => {
    const cleanup = getReport();
    return cleanup;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const isLoading = auditLoading || authUserInfo.permissions === null || loading;

  const {
    meta,
    stats,
    complaintCategories,
    maxCategory,
    dishComplaints,
    maxDish,
    timingComplaints,
    maxTiming,
    waiterStats,
    timingNote,
    waitersNote,
  } = useMemo(() => {
    if (!data) {
      return {
        meta: {}, stats: [], complaintCategories: [], maxCategory: 1,
        dishComplaints: [], maxDish: 1, timingComplaints: [], maxTiming: 1,
        waiterStats: [], timingNote: "", waitersNote: ""
      };
    }

    const { 
      stats: s = {}, 
      whats_being_complained_about: cats = [], 
      by_dish: dishes = [], 
      when_it_happens: whenObj = {}, 
      by_waiters_tables: waitObj = {}, 
      meta: m = {} 
    } = data;
    
    const timings = whenObj.slots || [];

    return {
      meta: m,
      stats: [
        { label: s.complaints_this_month?.label || "Complaints this month", value: s.complaints_this_month?.value ?? 0, color: "#0f172a" },
        { label: s.avg_resolution_time?.label || "Avg. resolution time (min)", value: s.avg_resolution_time?.value ?? 0, color: "#0a8d6e" },
        { label: s.bills_with_complaint?.label || "Bills with a complaint", value: `${s.bills_with_complaint?.value ?? 0}${s.bills_with_complaint?.unit ?? '%'}`, color: "#f59e0b" },
        { label: s.void_comp_cost?.label || "Void + comp cost", value: s.void_comp_cost?.formatted ?? "Rs 0", color: "#ef4444" },
      ],
      complaintCategories: cats,
      maxCategory: cats.length > 0 ? Math.max(...cats.map((item) => item.count)) : 1,
      dishComplaints: dishes,
      maxDish: dishes.length > 0 ? Math.max(...dishes.map((item) => item.count)) : 1,
      timingComplaints: timings,
      maxTiming: timings.length > 0 ? Math.max(...timings.map((item) => item.count)) : 1,
      waiterStats: waitObj.waiters || [],
      timingNote: whenObj.note,
      waitersNote: waitObj.note,
    };
  }, [data]);

  return (
    <>
      <Helmet>
        <title>{_t(t("Reports"))}</title>
      </Helmet>

      <main id="main" data-simplebar>

        <div className="container-fluid">
          <div className="row t-mt-10 gx-2">
            {/* Left Sidebar */}
            <div className="col-lg-2 col-xxl-2 t-mb-30 mb-lg-0">
              <ComplaintsSidebar />
            </div>

            {/* Right Main Content */}
            <div className="col-lg-10 col-xxl-10 t-mb-30 mb-lg-0">
              <CustomSpinLoader loading={isLoading} />
              <div
                className="fk-scroll--pos-menu table-bottom-info-hide"
                data-simplebar
              >
                <div className="board-container">
                  {/* Top Header & Selection UI */}
                  <div className="d-flex flex-wrap justify-content-between align-items-center mb-3 bg-white p-2 border-bottom border-1">
                    {/* Left: Title & Badges */}
                    <div className="board-title mb-3 mb-xl-0 d-flex flex-column justify-content-center">
                      <h1 className="mb-1" style={{ fontSize: '1.5rem', fontWeight: 'bold' }}>{_t(t("Reports"))}</h1>
                    </div>

                    {/* Right: Selection UI */}
                    <div className="d-flex flex-wrap align-items-center" style={{ gap: '12px' }}>
                      <div style={{ width: '150px' }}>
                        <DatePicker
                          selected={fromDate}
                          onChange={(date) => setFromDate(date)}
                          selectsStart
                          startDate={fromDate}
                          endDate={toDate}
                          className="form-control shadow-none bg-transparent"
                          style={{ border: 'none', borderBottom: '1px solid #cbd5e1', borderRadius: 0 }}
                          placeholderText={_t(t("From Date"))}
                        />
                      </div>
                      <div style={{ width: '150px' }}>
                        <DatePicker
                          selected={toDate}
                          onChange={(date) => setToDate(date)}
                          selectsEnd
                          startDate={fromDate}
                          endDate={toDate}
                          minDate={fromDate}
                          className="form-control shadow-none bg-transparent"
                          style={{ border: 'none', borderBottom: '1px solid #cbd5e1', borderRadius: 0 }}
                          placeholderText={_t(t("To Date"))}
                        />
                      </div>
                      <div style={{ width: '220px' }}>
                        <Select
                          options={branchForSearch || []}
                          getOptionLabel={(option) => option.name}
                          getOptionValue={(option) => option.id}
                          value={branch}
                          styles={{
                            ...customStyle,
                            control: (base) => ({
                              ...base,
                              border: 'none',
                              borderBottom: '1px solid #cbd5e1',
                              backgroundColor: 'transparent',
                              borderRadius: 0,
                              boxShadow: 'none',
                            })
                          }}
                          isClearable
                          classNamePrefix="select"
                          className="table-text"
                          onChange={(b) => setBranch(b)}
                          maxMenuHeight="200px"
                          placeholder={_t(t("Select branch"))}
                        />
                      </div>
                      <div>
                        <button
                          className="btn btn-primary sm-text py-2 px-4 rounded-lg font-weight-bold shadow-sm"
                          onClick={getReport}
                          disabled={loading}
                        >
                          {_t(t("GENERATE"))}
                        </button>
                      </div>
                    </div>
                  </div>

                  {/* Top Stats Metric Cards */}
                  <div className="report-stats-grid mb-4">
                    {stats.map((stat, index) => (
                      <div className="stat-card" key={index}>
                        <div className="stat-title">{_t(t(stat.label))}</div>
                        <div className="stat-value" style={{ color: stat.color }}>
                          {stat.value}
                        </div>
                      </div>
                    ))}
                  </div>

                  {/* 2-Column Grid Layout */}
                  <div className="ticket-layout-grid">
                    {/* Left Column */}
                    <div className="ticket-left-col">
                      {/* What's being complained about Card */}
                      <div className="ticket-card">
                        <div className="ticket-section-title mb-3">
                          {_t(t("What's being complained about"))}
                        </div>
                        <div className="report-bar-list">
                          {complaintCategories.length > 0 ? (
                            complaintCategories.map((item, idx) => (
                              <div className="report-bar-item" key={idx}>
                                <span className="report-bar-label">{_t(t(item.label || item.name))}</span>
                                <div className="report-bar-track">
                                  <div
                                    className="report-bar-fill"
                                    style={{ width: `${(item.count / maxCategory) * 100}%` }}
                                  />
                                </div>
                                <span className="report-bar-value">{item.count}</span>
                              </div>
                            ))
                          ) : (
                            <div className="text-muted">{_t(t("No data available"))}</div>
                          )}
                        </div>
                      </div>

                      {/* By dish Card */}
                      <div className="ticket-card">
                        <div className="ticket-section-title mb-3">
                          {_t(t("By dish"))}
                        </div>
                        <div className="report-bar-list">
                          {dishComplaints.length > 0 ? (
                            dishComplaints.map((item, idx) => (
                              <div className="report-bar-item" key={idx}>
                                <span className="report-bar-label">{_t(t(item.label || item.name))}</span>
                                <div className="report-bar-track">
                                  <div
                                    className="report-bar-fill"
                                    style={{ width: `${(item.count / maxDish) * 100}%` }}
                                  />
                                </div>
                                <span className="report-bar-value">{item.count}</span>
                              </div>
                            ))
                          ) : (
                            <div className="text-muted">{_t(t("No data available"))}</div>
                          )}
                        </div>
                      </div>
                    </div>

                    {/* Right Column */}
                    <div className="ticket-right-col">
                      {/* When it happens Card */}
                      <div className="ticket-card">
                        <div className="ticket-section-title mb-3">
                          {_t(t("When it happens"))}
                        </div>
                        <div className="report-bar-list">
                          {timingComplaints.length > 0 ? (
                            timingComplaints.map((item, idx) => (
                              <div className="report-bar-item" key={idx}>
                                <span className="report-bar-label">{_t(t(item.label || item.key))}</span>
                                <div className="report-bar-track">
                                  <div
                                    className="report-bar-fill"
                                    style={{ width: `${(item.count / maxTiming) * 100}%` }}
                                  />
                                </div>
                                <span className="report-bar-value">{item.count}</span>
                              </div>
                            ))
                          ) : (
                            <div className="text-muted">{_t(t("No data available"))}</div>
                          )}
                        </div>

                        {/* Timing Insight Box */}
                        {timingNote && (
                          <div className="report-callout mt-4">
                            {_t(t(timingNote))}
                          </div>
                        )}
                      </div>

                      {/* By waiter's tables Card */}
                      <div className="ticket-card">
                        <div className="ticket-section-title mb-3">
                          {_t(t("By waiter's tables"))}
                        </div>

                        <div style={{ maxHeight: '300px' }} className="pr-2 bill_modal_scroll">
                          <table className="report-waiter-table mb-0 w-100">
                            <thead className="bg-white" style={{ position: 'sticky', top: 0, zIndex: 1 }}>
                              <tr>
                                <th>{_t(t("Waiter"))}</th>
                                <th>{_t(t("Tables"))}</th>
                                <th>{_t(t("Complaints"))}</th>
                                <th>{_t(t("Rate"))}</th>
                              </tr>
                            </thead>
                            <tbody>
                              {waiterStats.length > 0 ? (
                                waiterStats.map((row, idx) => (
                                  <tr key={idx}>
                                    <td className="waiter-name font-weight-bold">{row.waiter || row.name || row.waiter_name}</td>
                                    <td>{row.tables}</td>
                                    <td>{row.complaints}</td>
                                    <td className="font-weight-bold">
                                      {row.rate || (row.percentage ? `${row.percentage}%` : "0%")}
                                    </td>
                                  </tr>
                                ))
                              ) : (
                                <tr>
                                  <td colSpan="4" className="text-center text-muted">
                                    {_t(t("No data available"))}
                                  </td>
                                </tr>
                              )}
                            </tbody>
                          </table>
                        </div>

                        {/* Waiter Rate Insight Box */}
                        {waitersNote && (
                          <div className="report-callout mt-4">
                            {_t(t(waitersNote))}
                          </div>
                        )}
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </main>
    </>
  );
}
